import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { blogsService } from '../../services/blogs';
import type { BlogPost } from '../../services/blogs';
import { newsData } from '../../mocks/news';
import Navbar from '../../components/feature/Navbar';
import LoadingSpinner from '../../components/feature/LoadingSpinner';

interface SearchFilters {
  category: string;
  author: string;
  dateRange: string;
}

export default function Search() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({
    category: 'Todas',
    author: '',
    dateRange: 'all'
  });
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const categories = ['Todas', 'Literatura', 'Teatro', 'Poesía', 'Ensayo', 'Blog'];

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      const posts = await blogsService.getPublishedBlogPosts();
      setBlogPosts(posts);
    } catch (error) {
      console.error('Error loading blog posts:', error);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() && filters.category === 'Todas' && !filters.author) {
      return;
    }

    setLoading(true);
    setHasSearched(true);

    // Simular búsqueda (en una app real, esto sería una llamada a la API)
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  };

  const getFilteredResults = () => {
    let results: any[] = [];

    // Filtrar blog posts de usuarios
    const filteredBlogs = blogPosts.filter(post => {
      const matchesQuery = !searchQuery.trim() || 
        post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.author_name.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = filters.category === 'Todas' || post.category === filters.category;
      
      const matchesAuthor = !filters.author || 
        post.author_name.toLowerCase().includes(filters.author.toLowerCase());

      return matchesQuery && matchesCategory && matchesAuthor;
    });

    // Filtrar noticias locales
    const filteredNews = newsData.filter(news => {
      const matchesQuery = !searchQuery.trim() || 
        news.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        news.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
        news.author.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesCategory = filters.category === 'Todas' || news.category === filters.category;
      
      const matchesAuthor = !filters.author || 
        news.author.toLowerCase().includes(filters.author.toLowerCase());

      return matchesQuery && matchesCategory && matchesAuthor;
    });

    // Combinar resultados
    results = [
      ...filteredBlogs.map(post => ({ ...post, type: 'blog' })),
      ...filteredNews.map(news => ({ ...news, type: 'news' }))
    ];

    // Ordenar por fecha (más recientes primero)
    results.sort((a, b) => {
      const dateA = new Date(a.created_at || a.date);
      const dateB = new Date(b.created_at || b.date);
      return dateB.getTime() - dateA.getTime();
    });

    return results;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const results = hasSearched ? getFilteredResults() : [];

  return (
    <div className="min-h-screen bg-lp-bg">
      <Navbar />
      
      <div className="pt-20 px-4 pb-8">
        {/* Header */}
        <div className="mb-6">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sepia-600 hover:text-sepia-800 font-ui text-sm mb-4 transition-colors duration-200"
          >
            <i className="ri-arrow-left-line"></i>
            Volver al inicio
          </button>
          
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <h1 className="font-headings text-xl text-sepia-900 mb-2">BUSCAR CONTENIDO</h1>
            <p className="text-sm text-sepia-600 font-ui">
              Encuentra artículos, blogs y noticias literarias
            </p>
          </div>
        </div>

        {/* Search Form */}
        <div className="vintage-paper aged-paper p-6 shadow-lg mb-6">
          {/* Search Input */}
          <div className="mb-4">
            <label className="block text-sm font-ui text-sepia-700 mb-2">
              Buscar por palabra clave
            </label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar por título, contenido o autor..."
                className="w-full p-3 pr-12 text-sm font-body text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-400/50"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sepia-600 hover:text-sepia-800 transition-colors"
              >
                <i className="ri-search-line"></i>
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-ui text-sepia-700 mb-2">
                Categoría
              </label>
              <select
                value={filters.category}
                onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="w-full p-3 text-sm font-body text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-400/50"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-ui text-sepia-700 mb-2">
                Autor
              </label>
              <input
                type="text"
                value={filters.author}
                onChange={(e) => setFilters({ ...filters, author: e.target.value })}
                placeholder="Buscar por nombre del autor..."
                className="w-full p-3 text-sm font-body text-sepia-800 bg-sepia-50/50 border border-sepia-300/50 rounded-[10%] focus:outline-none focus:ring-2 focus:ring-sepia-400/50"
              />
            </div>
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearch}
            disabled={loading}
            className="w-full mt-4 vintage-paper-subtle text-sepia-900 hover:bg-sepia-200/70 px-4 py-3 text-sm font-ui transition-all duration-300 rounded-[15%] hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Buscando...
              </>
            ) : (
              <>
                <i className="ri-search-line"></i>
                Buscar
              </>
            )}
          </button>
        </div>

        {/* Results */}
        {loading ? (
          <LoadingSpinner message="Buscando contenido..." />
        ) : hasSearched ? (
          <div className="vintage-paper aged-paper p-6 shadow-lg">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-headings text-lg text-sepia-900">
                RESULTADOS DE BÚSQUEDA
              </h2>
              <span className="text-sm text-sepia-600 font-ui">
                {results.length} resultado{results.length !== 1 ? 's' : ''}
              </span>
            </div>

            {results.length > 0 ? (
              <div className="space-y-4">
                {results.map((item, index) => (
                  <div key={`${item.type}_${item.id || index}`} className="vintage-paper-subtle p-4 rounded-[10%] border border-sepia-300/30">
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className="text-xs font-ui text-sepia-600 bg-sepia-100/50 px-2 py-1 rounded-[10%]">
                        {item.category}
                      </span>
                      <span className="text-xs text-sepia-500 font-ui">
                        {formatDate(item.created_at || item.date)}
                      </span>
                      {item.type === 'blog' && (
                        <span className="text-xs text-amber-700 font-ui bg-amber-100/50 px-2 py-1 rounded-[10%]">
                          Blog Usuario
                        </span>
                      )}
                      {item.type === 'news' && (
                        <span className="text-xs text-blue-700 font-ui bg-blue-100/50 px-2 py-1 rounded-[10%]">
                          Noticia
                        </span>
                      )}
                    </div>
                    
                    <h3 className="font-headings text-base text-sepia-900 mb-2 leading-snug">
                      {item.title}
                    </h3>
                    
                    <p className="text-sm text-sepia-700 font-body mb-3 leading-relaxed">
                      {item.excerpt}
                    </p>
                    
                    <div className="flex justify-between items-center text-xs text-sepia-600 font-ui border-t border-sepia-300/50 pt-3">
                      <span>Por: <strong>{item.author_name || item.author}</strong></span>
                      <button
                        onClick={() => {
                          if (item.type === 'blog') {
                            navigate(`/blog/${item.id}`);
                          } else {
                            navigate(`/news/${item.id}`);
                          }
                        }}
                        className="text-sepia-600 hover:text-sepia-800 transition-colors flex items-center gap-1"
                      >
                        <i className="ri-arrow-right-line"></i>
                        Leer más
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="ri-search-line text-3xl text-sepia-400 mb-3"></i>
                <h3 className="font-headings text-lg text-sepia-900 mb-2">
                  No se encontraron resultados
                </h3>
                <p className="text-sm text-sepia-700 font-body">
                  Intenta con otros términos de búsqueda o ajusta los filtros
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="vintage-paper aged-paper p-6 shadow-lg text-center">
            <i className="ri-search-2-line text-3xl text-sepia-400 mb-3"></i>
            <h3 className="font-headings text-lg text-sepia-900 mb-2">
              Busca contenido literario
            </h3>
            <p className="text-sm text-sepia-700 font-body">
              Utiliza los filtros de arriba para encontrar artículos, blogs y noticias
            </p>
          </div>
        )}

        {/* Vintage Background */}
        <div 
          className="fixed inset-0 pointer-events-none opacity-5 z-0"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=vintage%20library%20search%20scene%2C%20antique%20card%20catalog%20drawers%2C%20old%20wooden%20filing%20cabinets%2C%20magnifying%20glass%20on%20aged%20documents%2C%20scholarly%20research%20atmosphere%2C%20sepia%20tones%2C%20warm%20lighting%2C%20dusty%20books%20and%20manuscripts%2C%20academic%20investigation%20setting%2C%20brown%20and%20cream%20colors%2C%20nostalgic%20study%20environment&width=375&height=812&seq=search_bg&orientation=portrait')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    </div>
  );
}
